import { type CSSProperties } from "react";
import { showToast } from "../primitives";
import type { McpEndpoint } from "../../hooks/use-mcp-endpoints";

/**
 * Sprint 17 — one row in the Settings → Endpoints list (and reused conceptually
 * by the Cmd-K copy picker). Shows the scope badge, label, path, description,
 * tool count, last-used hint, and a copy-URL button.
 */
export function EndpointRow({ endpoint }: { endpoint: McpEndpoint }) {
  async function copy() {
    try {
      await navigator.clipboard.writeText(endpoint.url);
      showToast(`Copied ${endpoint.path} URL`);
    } catch {
      showToast("Couldn't copy to clipboard", "error", true);
    }
  }

  const badge = SCOPE_BADGE[endpoint.tool_scope] ?? DEFAULT_BADGE;
  // Sprint 24.11 — per-source endpoints (one connection / one wrapped API) carry a
  // per-tenant handle URL. Flag it inline so a user never assumes the slug-style URL
  // is shareable: it's auth-bound to this workspace.
  const isPerTenant = endpoint.tool_scope === "connection" || endpoint.tool_scope === "wrapper";
  const aliases = endpoint.legacy_aliases ?? [];

  return (
    <div style={row}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <span style={{ ...badgeStyle, color: badge.color, background: badge.bg }}>{badge.label}</span>
        <span style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)" }}>
          {endpoint.label}
        </span>
        <code style={pathStyle}>{endpoint.path}</code>
        {isPerTenant && (
          <i
            className="ti ti-lock"
            style={perTenantIcon}
            aria-label="Per-tenant URL"
            title="Per-tenant URL. Auth-bound to this workspace — different workspaces have different URLs even for the same connector."
          />
        )}
        <span style={{ flex: 1 }} />
        <span style={countStyle} title={`${endpoint.tool_count} tools exposed`}>
          {endpoint.tool_count} {endpoint.tool_count === 1 ? "tool" : "tools"}
        </span>
        <button onClick={copy} style={copyBtn} title={`Copy ${endpoint.url}`}>
          <i className="ti ti-copy" style={{ fontSize: 14 }} aria-hidden="true" />
          Copy URL
        </button>
      </div>
      <div style={{ fontSize: 12.5, color: "var(--color-text-secondary)", lineHeight: 1.45 }}>
        {endpoint.description}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 6 }}>
        {endpoint.requires_role === "owner" && (
          <span style={metaHint}>
            <i className="ti ti-shield-lock" style={{ fontSize: 12 }} aria-hidden="true" /> Owner only
          </span>
        )}
        <span style={metaHint}>
          <i className="ti ti-clock" style={{ fontSize: 12 }} aria-hidden="true" />
          {endpoint.last_seen ? `Last used ${relativeTime(endpoint.last_seen)}` : "Not used this session"}
        </span>
        {aliases.length > 0 && (
          <span
            style={metaHint}
            title={`Older URL${aliases.length > 1 ? "s" : ""} that still resolve here: ${aliases.join(", ")}`}
          >
            <i className="ti ti-history" style={{ fontSize: 12 }} aria-hidden="true" />
            Legacy URL{aliases.length > 1 ? "s" : ""} still work
          </span>
        )}
      </div>
    </div>
  );
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "recently";
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

const DEFAULT_BADGE = { label: "CONTENT", color: "var(--c-green-700)", bg: "var(--c-green-50)" };

const SCOPE_BADGE: Record<string, { label: string; color: string; bg: string }> = {
  all: { label: "ALL", color: "var(--c-blue-700)", bg: "var(--c-blue-50)" },
  admin: { label: "ADMIN", color: "var(--c-purple-700)", bg: "var(--c-purple-50)" },
  user: DEFAULT_BADGE,
  lite: { label: "LITE", color: "var(--c-amber-700)", bg: "var(--c-amber-50)" },
  connection: { label: "CONNECTION", color: "var(--color-text-secondary)", bg: "var(--color-background-secondary)" },
  wrapper: { label: "WRAPPER", color: "var(--color-text-secondary)", bg: "var(--color-background-secondary)" },
};

const row: CSSProperties = {
  padding: "14px 16px",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-md)",
  background: "var(--color-background-primary)",
};

const badgeStyle: CSSProperties = {
  fontSize: 9.5,
  fontWeight: 600,
  letterSpacing: "0.05em",
  padding: "2px 6px",
  borderRadius: 4,
  flexShrink: 0,
};

const pathStyle: CSSProperties = {
  fontSize: 12,
  fontFamily: "var(--font-mono, ui-monospace, monospace)",
  color: "var(--color-text-tertiary)",
};

const perTenantIcon: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-tertiary)",
  cursor: "help",
  flexShrink: 0,
};

const countStyle: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  fontVariantNumeric: "tabular-nums",
  flexShrink: 0,
};

const copyBtn: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  fontSize: 12,
  padding: "4px 10px",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-md)",
  background: "var(--color-background-secondary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  flexShrink: 0,
};

const metaHint: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontSize: 11,
  color: "var(--color-text-tertiary)",
};
